<template>
  <transition name="overlay-fade">
      <div
        v-if="modelValue"
        class="sk-dialog__overlay"
       data-testid="components-ui-sk-dialog-sk-dialog-div">
      <div 
        ref="dialogRef"
        v-click-outside="onClickOutside"
        class="sk-dialog"
       data-testid="components-ui-sk-dialog-sk-dialog-div-2">
        <template v-if="$slots.header">
          <slot name="header" />
        </template>

        <template v-else>
          <div class="p-5 flex justify-between" data-testid="components-ui-sk-dialog-sk-dialog-div-3">
            <div data-testid="components-ui-sk-dialog-sk-dialog-div-4">
              <div class="text-[24px] text-[#292929] font-bold leading-[120%]" data-testid="components-ui-sk-dialog-sk-dialog-div-5">
                {{ title }}
              </div>
              <div class="text-sm text-[#A6AEB8] leading-[140%]" data-testid="components-ui-sk-dialog-sk-dialog-div-6">
                {{ subtitle }}
              </div>
            </div>

            <div
              class="h-[28px] w-[28px] rounded-full bg-[#EDEFF3] hover:bg-[#EDEFF3]/75 cursor-pointer flex justify-center items-center transition-opacity duration-300"
              @click="closeDialog"
             data-testid="components-ui-sk-dialog-sk-dialog-div-7">
              <UiIcon
                color="#999999"
                width="17"
                height="17"
                value="close-v3"
              data-testid="components-ui-sk-dialog-sk-dialog-ui-icon" />
            </div>
          </div>
        </template>

        <div class="sk-dialog__divider" data-testid="components-ui-sk-dialog-sk-dialog-div-8" />

        <div class="sk-dialog__content" data-testid="components-ui-sk-dialog-sk-dialog-div-9">
          <slot name="content" />
        </div>

        <div class="sk-dialog__divider" data-testid="components-ui-sk-dialog-sk-dialog-div-10" />

        <slot name="footer" />
      </div>
    </div>
  </transition>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { isIgnored } from '@/composables/useClickOutsideIgnore.js'

import UiIcon from '@/components/ui/icon.vue';

const dialogRef = ref(null)

const emit = defineEmits(["update:modelValue"])

defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  width: {
    type: String,
    default: '400px'
  },
  title: {
    type: String,
    default: ''
  },
  subtitle: {
    type: String,
    default: ''
  }
})

const handleKey = (e) => {
  if (e.keyCode === 27) {
    closeDialog()
  }
};

const closeDialog = () => {
  emit('update:modelValue', false);
}

onMounted(() => {
  window.addEventListener('keyup', handleKey);
})

onUnmounted(() => {
  window.removeEventListener('keyup', handleKey);
})

const onClickOutside = (event) => {
  if(dialogRef.value.contains(event.target)) return 
  if(isIgnored(event.target)) return

  closeDialog()
}
</script>

<style lang="scss" scoped>
.sk-dialog {
  max-height: 90vh;
  width: v-bind(width);
  transition: opacity 0.3s ease-out, transform 0.3s ease-out, box-shadow 0.3s ease-out;
  box-shadow: 0px 8px 40px 0px #0000000A;
  background-color: #FFFFFF;
  border-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &__overlay {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0, 0, 0, 0.2);
    position: fixed;
    z-index: 2000;
    inset: 0;
  }

  &__content {
    flex: 1;
    min-height: 0;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-color: #A6AEB8 #EDEFF3;
    scrollbar-width: thin;
  }

  &__divider {
    border-top: 1px solid #DCE1E7;
  }
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.2s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.overlay-fade-enter-active .sk-dialog,
.overlay-fade-leave-active .sk-dialog {
  transition: opacity 0.2s ease-out, transform 0.2s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.overlay-fade-enter-from .sk-dialog,
.overlay-fade-leave-to .sk-dialog {
  opacity: 0;
  transform: scale(0.9);
}
</style>